import type { LatLon, Vec3 } from './types';

/**
 * Convert a geographic coordinate to a Cartesian vector on the sphere.
 *
 * Uses the same convention as the Atlas prototype so that the NASA Blue
 * Marble texture (equirectangular projection) aligns correctly when wrapped:
 *
 *   - +Y is "up" through the North Pole
 *   - +X points through (lat=0, lon=0) (the Gulf of Guinea, roughly)
 *   - +Z points through (lat=0, lon=+90) (Indian Ocean)
 *
 * Default `radius` is 1 (unit sphere). Pass a different radius to place the
 * point slightly off-surface for borders, markers lifted above terrain, etc.
 */
export function latLonToVec3(lat: number, lon: number, radius = 1): Vec3 {
  const phi = ((90 - lat) * Math.PI) / 180; // polar angle from +Y axis
  const theta = ((lon + 180) * Math.PI) / 180; // azimuth, shifted so the texture aligns
  return {
    x: -radius * Math.sin(phi) * Math.cos(theta),
    y: radius * Math.cos(phi),
    z: radius * Math.sin(phi) * Math.sin(theta),
  };
}

/**
 * Convert a Cartesian vector back to a geographic coordinate.
 * Inverse of `latLonToVec3`; the two should round-trip to within floating-point
 * precision.
 */
export function vec3ToLatLon(v: Vec3): LatLon {
  const r = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  const lat = 90 - (Math.acos(v.y / r) * 180) / Math.PI;
  let lon = (Math.atan2(v.z, -v.x) * 180) / Math.PI - 180;
  // atan2 can produce lon values slightly below -180 after the shift; normalize.
  if (lon < -180) lon += 360;
  if (lon > 180) lon -= 360;
  return { lat, lon };
}

/** Convert degrees to radians. */
export function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Convert radians to degrees. */
export function toDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}
